/* eslint-disable react-hooks/exhaustive-deps */
import { Button, ButtonPrimary } from '@/components/button'
import { ArrowBack } from '@/components/icons'
import { ROUTE_DASHBOARD, ROUTE_FORGOT_PASSWORD, ROUTE_REST_PASSWORD } from '@/constants/Routes'
import { useAppSelector } from '@/hooks/useRedux'
import { authSelector } from '@/pages/auth/selectors'
import { initiateEmail } from '@/services'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import OTPInput from 'react-otp-input'
import { useLocation, useNavigate } from 'react-router-dom'
import { eventTracker } from '@/config/trackingFunctions'
import { TRACKING_TYPES } from '@/constants/TrackingConstants'

export const VerifyEmail = () => {
  const { t } = useTranslation()

  const navigate = useNavigate()
  const location = useLocation()

  const { token } = useAppSelector(authSelector)

  const [verifyDigits, setVerifyDigits] = useState('')
  const [resending, setResending] = useState(false)

  const onGoBack = () => navigate(-1)

  // Same contract as the email step: the response is never inspected. Rendering the server's
  // `message` here would put a per-address, server-authored string back on screen — the exact
  // enumeration surface the reset flow was hardened against. The copy below is fixed and local.
  const onResendMail = async () => {
    if (resending) return
    setResending(true)
    try {
      await initiateEmail(location.state.email)
    } catch {
      // intentionally ignored — a resend must look identical whatever the server says
    } finally {
      setResending(false)
    }
  }

  const onClickVerify = () => {
    navigate(ROUTE_REST_PASSWORD, {
      state: {
        resetData: {
          email: location.state.email,
          code: verifyDigits,
        },
      },
    })
    eventTracker(TRACKING_TYPES.VERIFY_EMAIL, { email: location.state.email })
  }

  useEffect(() => {
    if (token) {
      navigate(ROUTE_DASHBOARD, { relative: 'path', replace: true })
    }
  }, [token])

  useEffect(() => {
    if (!location.state) navigate(ROUTE_FORGOT_PASSWORD, { relative: 'path', replace: true })
  }, [location])

  return (
    <div className="w-screen h-screen bg-white flex sm:justify-center sm:items-center">
      <div className="flex flex-col justify-between sm:justify-start pb-14 sm:pb-0 w-full px-4 sm:px-0 sm:w-[390px] gap-y-4">
        <div className="flex relative flex-col sm:flex-row sm:items-center mt-8 sm:mt-0 gap-y-4 pb-0 sm:pb-6">
          <Button onClick={onGoBack} className="hover:opacity-50 flex sm:absolute sm:-left-12 sm:top-4">
            <ArrowBack className="text-navy-900" />
          </Button>
          <div className="flex flex-col gap-y-2 p-0 sm:p-4">
            <h1 className="text-2xl xs:text-3xl font-bold text-navy-900">{t('auth.checkYourEmail')}</h1>
            <p className="text-sm text-navy-900 text-opacity-70">{t('auth.verifyEmailDesc')}</p>
          </div>
        </div>

        <div className="flex flex-col gap-y-4 px-3">
          <span className="text-base text-navy-900 font-semibold text-center">{t('common.textDigitCode')}</span>
          <span className="text-sm text-center text-green-400">{t('auth.resetCodeSentGeneric')}</span>

          <OTPInput
            inputStyle="flex-1 max-w border-[1px] rounded-2xl aspect-square max-w-[64px]"
            containerStyle="justify-between gap-x-4 sm:gap-x-10 mx-4"
            renderInput={(props) => <input {...props} />}
            numInputs={4}
            value={verifyDigits}
            onChange={setVerifyDigits}
            shouldAutoFocus
          />
        </div>

        <div className="flex flex-row justify-between mx-4 gap-x-4 px-3">
          <ButtonPrimary
            onClick={onResendMail}
            type="button"
            className="w-1/2 text-md rounded-full px-5 py-3 disabled:opacity-60 bg-gray-500"
            disabled={resending}
            title={t('common.resend')}
          />
          <ButtonPrimary
            onClick={onClickVerify}
            type="button"
            className="outline-none w-1/2 text-md font-medium bg-primary-500 rounded-full text-white px-5 py-3 disabled:opacity-40"
            disabled={verifyDigits.length !== 4}
            title={t('common.verify')}
          />
        </div>
      </div>
    </div>
  )
}
